/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useMemo, useState } from 'react'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import { Table } from 'react-bootstrap-v5'
import { Link, useHistory } from 'react-router-dom'
import { KTSVG } from '../../_metronic/helpers'
import Swal from 'sweetalert2'
import Select from 'react-select'
import { AppContext } from '../context/AppContext'

interface SelectOption {
	label: string;
	value: number;
}

interface Material {
	idItems: number;
	itemName: string;
	categoryName?: string;
	quantity: number;
	unitName: string;
}

interface SourceProduct {
	idProducts: number;
	itemName: string;
	productName: string;
	packing: string;
	size: number;
	itemUnitName: string;
	available: number;
}

interface TargetRow {
	idProducts: number;
	productName: string;
	packing: string;
	size: number;
	itemUnitName: string;
	packingUnit: string;
	materials: Material[];
	quantity: string;
}

interface ItemLine {
	idItems: number;
	itemName: string;
	categoryName?: string;
	unitName: string;
	quantity: number;
}

interface Preview {
	source: { rate: number | null; amount: number; available: number };
	targets: Array<{ idProducts: number; costPerUnit: number; totalCost: number }>;
	issues: Array<{ idItems: number; rate: number | null; amount: number }>;
	returns: Array<{ idItems: number; rate: number | null; amount: number }>;
	issueAmount: number;
	returnAmount: number;
	totalCost: number;
	problems: string[];
}

const TOLERANCE = 0.00001

const num = (value: any, digits = 5) => {
	const parsed = Number(value)
	return Number.isFinite(parsed) ? parseFloat(parsed.toFixed(digits)) : 0
}

const withUnit = (value: string | number, unit: string) => {
	const text = value === null || value === undefined || value === '' ? '' : String(value)
	return text && unit ? `${text} ${unit}` : text
}

/** Packing BOM lines for a number of packs, summed per item across pack sizes. */
const bomLines = (boms: Array<{ materials: Material[]; packs: number }>): ItemLine[] => {
	const lines: Record<number, ItemLine> = {}
	boms.forEach(({ materials, packs }) => {
		if (packs <= 0) {
			return
		}
		materials.forEach((material) => {
			if (!lines[material.idItems]) {
				lines[material.idItems] = { ...material, quantity: 0 }
			}
			lines[material.idItems].quantity = num(lines[material.idItems].quantity + Number(material.quantity) * packs)
		})
	})
	return Object.values(lines)
}

function ProductConversionCreate() {
	const history = useHistory()
	const { currentUser } = useContext(AppContext)
	const canViewCost = Number(currentUser?.id_roles) === 3 || Number(currentUser?.id_roles) === 4

	const [projects, setProjects] = useState<SelectOption[]>([])
	const [project, setProject] = useState<SelectOption | null>(null)
	const [warehouses, setWarehouses] = useState<SelectOption[]>([])
	const [warehouse, setWarehouse] = useState<SelectOption | null>(null)
	const [packingItemWarehouse, setPackingItemWarehouse] = useState<SelectOption | null>(null)
	const [conversionDate, setConversionDate] = useState(new Date().toISOString().slice(0, 10))
	const [remarks, setRemarks] = useState('')

	const [sourceProducts, setSourceProducts] = useState<SourceProduct[]>([])
	const [source, setSource] = useState<SourceProduct | null>(null)
	const [sourceQuantity, setSourceQuantity] = useState('')
	const [sourceMaterials, setSourceMaterials] = useState<Material[]>([])
	const [targets, setTargets] = useState<TargetRow[]>([])

	const [removedIssues, setRemovedIssues] = useState<number[]>([])
	const [removedReturns, setRemovedReturns] = useState<number[]>([])

	const [preview, setPreview] = useState<Preview | null>(null)
	const [previewError, setPreviewError] = useState<string | null>(null)
	const [previewLoading, setPreviewLoading] = useState(false)
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const options = (resp.data || []).map((item: any) => ({ label: item.name, value: Number(item.id) }))
				setProjects(options)
				if (options.length === 1) {
					setProject(options[0])
				}
			})
			.catch(() => setProjects([]))
	}, [])

	useEffect(() => {
		setWarehouses([])
		setWarehouse(null)
		setPackingItemWarehouse(null)
		if (!project) {
			return
		}
		fetch(process.env.REACT_APP_API_URL + 'warehouse/' + project.value + '/list', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const options = (resp.success && Array.isArray(resp.data))
					? resp.data.map((item: any) => ({ label: item.name, value: Number(item.id_warehouses) }))
					: []
				setWarehouses(options)
				if (options.length > 0) {
					setWarehouse(options[0])
					setPackingItemWarehouse(options[0])
				}
			})
			.catch(() => setWarehouses([]))
	}, [project?.value])

	useEffect(() => {
		setSourceProducts([])
		setSource(null)
		if (!project || !warehouse) {
			return
		}
		const params = new URLSearchParams({ idProjects: String(project.value), idWarehouses: String(warehouse.value) })
		fetch(process.env.REACT_APP_API_URL + 'product-conversions/source-products?' + params, getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => setSourceProducts(resp.success ? resp.data : []))
			.catch(() => setSourceProducts([]))
	}, [project?.value, warehouse?.value])

	useEffect(() => {
		setTargets([])
		setSourceMaterials([])
		setSourceQuantity('')
		setRemovedIssues([])
		setRemovedReturns([])
		if (!source) {
			return
		}
		fetch(process.env.REACT_APP_API_URL + 'product-conversions/targets/' + source.idProducts, getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				if (!resp.success) {
					Swal.fire({ icon: 'error', title: 'Error', text: resp.errorMessage || 'Unable to load pack sizes.' })
					return
				}
				setSourceMaterials(resp.sourceMaterials || [])
				setTargets((resp.data || []).map((target: any) => ({
					...target,
					size: Number(target.size) || 0,
					materials: target.materials || [],
					quantity: '',
				})))
			})
			.catch(() => setTargets([]))
	}, [source?.idProducts])

	const sourceQty = parseFloat(sourceQuantity) || 0
	const sourceLiters = sourceQty * (source ? Number(source.size) : 0)
	const targetLiters = targets.reduce((sum, target) => sum + (parseFloat(target.quantity) || 0) * target.size, 0)
	const litersMatch = sourceLiters > 0 && Math.abs(sourceLiters - targetLiters) <= TOLERANCE
	const unit = source?.itemUnitName || ''

	// the target pack sizes' packing BOMs are issued; the source pack size's BOM can be recovered, line by line
	const issues = useMemo(
		() => bomLines(targets.map((target) => ({ materials: target.materials, packs: parseFloat(target.quantity) || 0 }))),
		[targets]
	)
	const recoverable = useMemo(
		() => bomLines([{ materials: sourceMaterials, packs: sourceQty }]),
		[sourceMaterials, sourceQty]
	)
	const keptIssues = issues.filter((line) => !removedIssues.includes(line.idItems))
	const keptReturns = recoverable.filter((line) => !removedReturns.includes(line.idItems))

	const toggleIssue = (idItems: number, keep: boolean) => {
		setRemovedIssues((prev) => keep ? prev.filter((id) => id !== idItems) : [...prev, idItems])
	}

	const toggleReturn = (idItems: number, keep: boolean) => {
		setRemovedReturns((prev) => keep ? prev.filter((id) => id !== idItems) : [...prev, idItems])
	}

	const payload = useMemo(() => ({
		idProjects: project?.value,
		idWarehouses: warehouse?.value,
		idPackingItemWarehouses: packingItemWarehouse?.value,
		conversionDate,
		idSourceProducts: source?.idProducts,
		sourceQuantity: sourceQty,
		remarks,
		rows: targets
			.filter((target) => (parseFloat(target.quantity) || 0) > 0)
			.map((target) => ({ idProducts: target.idProducts, quantity: parseFloat(target.quantity) })),
		issues: keptIssues.map((line) => ({ idItems: line.idItems })),
		returns: keptReturns.map((line) => ({ idItems: line.idItems, quantity: line.quantity })),
	}), [project?.value, warehouse?.value, packingItemWarehouse?.value, conversionDate, source?.idProducts, sourceQty, remarks, targets, keptIssues.map((line) => line.idItems).join(','), keptReturns.map((line) => line.idItems).join(',')])

	const readyToCheck = Boolean(payload.idProjects && payload.idWarehouses && payload.idPackingItemWarehouses && payload.idSourceProducts
		&& conversionDate && sourceQty > 0 && payload.rows.length > 0 && litersMatch)

	useEffect(() => {
		setPreview(null)
		setPreviewError(null)
		if (!readyToCheck) {
			return
		}

		let cancelled = false
		const timer = setTimeout(() => {
			setPreviewLoading(true)
			fetch(process.env.REACT_APP_API_URL + 'product-conversions/preview', postRequestOptions(payload))
				.then((resp) => resp.json())
				.then((resp) => {
					if (cancelled) {
						return
					}
					if (!resp.success) {
						setPreviewError(resp.errorMessage || resp.errors || 'Unable to check the conversion.')
						return
					}
					setPreview(resp.data)
				})
				.catch(() => {
					if (!cancelled) {
						setPreviewError('Unable to check the conversion.')
					}
				})
				.finally(() => {
					if (!cancelled) {
						setPreviewLoading(false)
					}
				})
		}, 400)

		return () => {
			cancelled = true
			clearTimeout(timer)
		}
	}, [readyToCheck, JSON.stringify(payload)])

	const updateTarget = (idProducts: number, quantity: string) => {
		setTargets((prev) => prev.map((target) => target.idProducts === idProducts ? { ...target, quantity } : target))
	}

	const canSave = Boolean(readyToCheck && preview && preview.problems.length === 0 && !previewLoading && !previewError && !saving)

	const handleSave = async (event: React.FormEvent) => {
		event.preventDefault()
		if (!canSave) {
			let message = 'Complete the form first.'
			if (sourceQty > 0 && !litersMatch) {
				message = `Target quantity (${num(targetLiters)} ${unit}) must equal the source quantity (${num(sourceLiters)} ${unit}).`
			} else if (previewError) {
				message = previewError
			} else if (preview?.problems.length) {
				message = preview.problems.join(' ')
			} else if (previewLoading) {
				message = 'The conversion is still being checked.'
			}
			Swal.fire({ icon: 'warning', title: 'Validation', text: message })
			return
		}

		const confirm = await Swal.fire({
			title: 'Are you sure?',
			text: `This will stock out ${num(sourceQty)} x ${source?.packing}, stock in the target pack sizes and move the packing items.`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#ec008c',
			cancelButtonText: 'Cancel',
			confirmButtonText: 'Yes, Save',
		})
		if (!confirm.isConfirmed) {
			return
		}

		setSaving(true)
		try {
			const response = await fetch(process.env.REACT_APP_API_URL + 'product-conversions', postRequestOptions(payload))
			const data = await response.json()
			if (data.success) {
				Swal.fire({ icon: 'success', title: 'Success', text: data.successMessage })
					.then(() => history.push(`/product-conversion-details/${data.data.id_product_conversions}`))
			} else {
				Swal.fire({ icon: 'error', title: 'Error', text: data.errorMessage || data.errors || 'Save failed.' })
			}
		} catch (error) {
			Swal.fire({ icon: 'error', title: 'Error', text: 'An error occurred.' })
		} finally {
			setSaving(false)
		}
	}

	const targetCost = (idProducts: number) => preview?.targets.find((target) => target.idProducts === idProducts)
	const lineCost = (list: Preview['issues'] | undefined, idItems: number) => list?.find((line) => line.idItems === idItems)

	const renderItemLines = (
		title: string,
		hint: string,
		lines: ItemLine[],
		costs: Preview['issues'] | undefined,
		emptyText: string,
		keep?: { label: string; isKept: (idItems: number) => boolean; toggle: (idItems: number, keep: boolean) => void }
	) => {
		const columnCount = 5 + (keep ? 1 : 0) + (canViewCost ? 2 : 0)
		return (
			<>
				<h5 className="mt-5 mb-1">{title}</h5>
				<div className="text-muted small mb-3">{hint}</div>
				<Table size="sm" bordered hover>
					<thead>
						<tr>
							{keep && <th style={{ width: '60px' }}>{keep.label}</th>}
							<th>SL</th>
							<th>Item</th>
							<th>Category</th>
							<th>Quantity</th>
							<th>Unit</th>
							{canViewCost && <th>Rate</th>}
							{canViewCost && <th>Amount</th>}
						</tr>
					</thead>
					<tbody>
						{lines.length === 0 && (
							<tr>
								<td colSpan={columnCount} className="text-center text-muted">{emptyText}</td>
							</tr>
						)}
						{lines.map((line, index) => {
							const kept = keep ? keep.isKept(line.idItems) : true
							const cost = kept ? lineCost(costs, line.idItems) : undefined
							return (
								<tr key={line.idItems} className={kept ? '' : 'text-muted text-decoration-line-through'}>
									{keep &&
										<td className="text-center">
											<input
												type="checkbox"
												className="form-check-input"
												checked={kept}
												onChange={(event) => keep.toggle(line.idItems, event.target.checked)}
											/>
										</td>
									}
									<td>{index + 1}</td>
									<td>{line.itemName}</td>
									<td>{line.categoryName}</td>
									<td>{num(line.quantity)}</td>
									<td>{line.unitName}</td>
									{canViewCost && <td>{cost ? (cost.rate === null ? <span className="text-danger">Missing</span> : num(cost.rate, 4)) : ''}</td>}
									{canViewCost && <td>{cost && cost.rate !== null ? num(cost.amount, 2) : ''}</td>}
								</tr>
							)
						})}
					</tbody>
				</Table>
			</>
		)
	}

	return (
		<div className="card card-custom">
			<div className="card-header">
				<div className="card-title">
					<KTSVG path="/media/svg/icons/Text/Bullet-list.svg" className="svg-icon-1 svg-icon-primary" />
					<span className="text fw-bolder" style={{ marginLeft: '10px' }}>
						Create Product Conversion
					</span>
				</div>
				<div className="card-toolbar">
					<Link to="/product-conversions" className="btn btn-sm btn-secondary">Back</Link>
				</div>
			</div>
			<div className="card-body">
				<form onSubmit={handleSave}>
					<div className="row">
						<div className="col-md-4 mb-4">
							<label className="form-label required">Project</label>
							<Select value={project} options={projects} onChange={(option) => setProject(option as SelectOption)} placeholder="Select project" />
						</div>
						<div className="col-md-4 mb-4">
							<label className="form-label required">Finished Good Warehouse</label>
							<Select value={warehouse} options={warehouses} onChange={(option) => setWarehouse(option as SelectOption)} placeholder="Select warehouse" />
						</div>
						<div className="col-md-4 mb-4">
							<label className="form-label required">Packing Item Warehouse</label>
							<Select value={packingItemWarehouse} options={warehouses} onChange={(option) => setPackingItemWarehouse(option as SelectOption)} placeholder="Select warehouse" />
						</div>
						<div className="col-md-4 mb-4">
							<label className="form-label required">Conversion Date</label>
							<input type="date" className="form-control" value={conversionDate} onChange={(event) => setConversionDate(event.target.value)} />
						</div>
						<div className="col-md-8 mb-4">
							<label className="form-label">Remarks</label>
							<input type="text" className="form-control" value={remarks} onChange={(event) => setRemarks(event.target.value)} />
						</div>
					</div>

					<h5 className="mt-3 mb-3">Convert From</h5>
					<div className="row">
						<div className="col-md-6 mb-4">
							<label className="form-label required">Source Pack Size</label>
							<Select
								value={source ? { label: `${source.itemName} - ${withUnit(source.packing, source.itemUnitName)}`, value: source.idProducts } : null}
								options={sourceProducts.map((product) => ({
									label: `${product.itemName} - ${withUnit(product.packing, product.itemUnitName)} (Stock: ${num(product.available)})`,
									value: product.idProducts,
								}))}
								onChange={(option: any) => setSource(sourceProducts.find((product) => product.idProducts === option?.value) || null)}
								placeholder={warehouse ? (sourceProducts.length ? 'Select pack size' : 'No finished goods in this warehouse') : 'Select a warehouse first'}
							/>
						</div>
						<div className="col-md-3 mb-4">
							<label className="form-label required">Quantity</label>
							<input
								type="number"
								className="form-control"
								step="0.01"
								min="0"
								value={sourceQuantity}
								disabled={!source}
								onChange={(event) => setSourceQuantity(event.target.value)}
							/>
							{source && <div className="text-muted small mt-1">Available: {num(source.available)}</div>}
						</div>
						<div className="col-md-3 mb-4">
							<label className="form-label">Total</label>
							<input type="text" className="form-control" readOnly value={withUnit(num(sourceLiters), unit)} />
							{canViewCost && preview?.source.rate !== null && preview?.source.rate !== undefined &&
								<div className="text-muted small mt-1">Avg rate: {num(preview.source.rate, 4)} / pack, value {num(preview.source.amount, 2)}</div>
							}
						</div>
					</div>

					<h5 className="mt-3 mb-3">Convert To</h5>
					<Table size="sm" bordered hover>
						<thead>
							<tr>
								<th>SL</th>
								<th>Packing</th>
								<th>Size</th>
								<th>Packing Unit</th>
								<th style={{ width: '180px' }}>Quantity</th>
								<th>Total</th>
								{canViewCost && <th>Cost/Per Unit</th>}
								{canViewCost && <th>Total Cost</th>}
							</tr>
						</thead>
						<tbody>
							{targets.length === 0 && (
								<tr>
									<td colSpan={canViewCost ? 8 : 6} className="text-center text-muted">
										{source ? 'No other active pack size of this product.' : 'Select the source pack size first.'}
									</td>
								</tr>
							)}
							{targets.map((target, index) => {
								const qty = parseFloat(target.quantity) || 0
								const cost = targetCost(target.idProducts)
								return (
									<tr key={target.idProducts}>
										<td>{index + 1}</td>
										<td>{withUnit(target.packing, target.itemUnitName)}</td>
										<td>{withUnit(num(target.size), target.itemUnitName)}</td>
										<td>{target.packingUnit}</td>
										<td>
											<input
												type="number"
												className="form-control form-control-sm"
												step="0.01"
												min="0"
												value={target.quantity}
												onChange={(event) => updateTarget(target.idProducts, event.target.value)}
											/>
										</td>
										<td>{qty > 0 ? withUnit(num(qty * target.size), target.itemUnitName) : ''}</td>
										{canViewCost && <td>{qty > 0 && cost ? num(cost.costPerUnit, 4) : ''}</td>}
										{canViewCost && <td>{qty > 0 && cost ? num(cost.totalCost, 2) : ''}</td>}
									</tr>
								)
							})}
						</tbody>
						{targets.length > 0 &&
							<tfoot>
								<tr>
									<td colSpan={5} className="text-end fw-bold">Total</td>
									<td className={`fw-bold ${sourceQty > 0 && !litersMatch ? 'text-danger' : ''}`}>
										{withUnit(num(targetLiters), unit)} / {withUnit(num(sourceLiters), unit)}
									</td>
									{canViewCost && <td></td>}
									{canViewCost && <td className="fw-bold">{preview ? num(preview.totalCost, 2) : ''}</td>}
								</tr>
							</tfoot>
						}
					</Table>
					{sourceQty > 0 && targetLiters > 0 && !litersMatch &&
						<div className="text-danger mb-3">
							Target total ({withUnit(num(targetLiters), unit)}) must equal the source total ({withUnit(num(sourceLiters), unit)}).
						</div>
					}

					{renderItemLines(
						'Packing Items to Issue',
						'The Packing BOM of the pack sizes converted to, issued from the packing item warehouse. Untick an item that should not be issued.',
						issues,
						preview?.issues,
						'Enter the quantity of a pack size to convert to.',
						{ label: 'Issue', isKept: (idItems) => !removedIssues.includes(idItems), toggle: toggleIssue }
					)}

					{renderItemLines(
						'Recovered Packing Items',
						'The Packing BOM of the source pack size. Keep what can be reused (it goes back to the packing item warehouse); untick what is damaged or thrown away.',
						recoverable,
						preview?.returns,
						source && sourceQty > 0 ? 'The source pack size has no Packing BOM.' : 'Enter the source quantity.',
						{ label: 'Keep', isKept: (idItems) => !removedReturns.includes(idItems), toggle: toggleReturn }
					)}

					{previewLoading && <div className="text-muted mt-4">Checking stock and rates...</div>}
					{previewError && <div className="alert alert-danger mt-4 mb-0">{previewError}</div>}
					{preview && preview.problems.length > 0 &&
						<div className="alert alert-danger mt-4 mb-0">
							<ul className="mb-0">
								{preview.problems.map((problem) => <li key={problem}>{problem}</li>)}
							</ul>
						</div>
					}

					<div className="mt-5">
						<button type="submit" className="btn btn-primary" disabled={saving}>
							{saving ? 'Saving...' : 'Save Conversion'}
						</button>
					</div>
				</form>
			</div>
		</div>
	)
}

export default ProductConversionCreate
